/**
 * PlatformBlogPostPage.jsx — a BookingFlows-authored blog post at /blog/{slug}.
 * SEO-first: title/description/keywords meta, OG tags, JSON-LD Article schema.
 * CTAs everywhere point to "Learn more" (support center) or "Sign up".
 */
import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';
import ReactMarkdown from 'react-markdown';
import { ChevronLeft, Loader2, Calendar, ArrowRight, LifeBuoy, Rocket } from 'lucide-react';
import { BACKEND_URL } from '../config/api';

const fetcher = (url) => fetch(url).then((r) => {
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
});

const CATEGORY_LABELS = {
  feature: 'Feature',
  integration: 'Integration',
  comparison: 'Comparison',
  story: 'Story',
  'how-to': 'How-to',
};

const setMeta = (name, content, attr = 'name') => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
};

export default function PlatformBlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const { data: post, error, isLoading } = useSWR(
    slug ? `${BACKEND_URL}/api/v2/public/platform-blog/${slug}` : null,
    fetcher,
    { revalidateOnFocus: false }
  );

  useEffect(() => {
    if (!post) return;
    const baseTitle = post.seo_title || `${post.title} | BookingFlows`;
    document.title = baseTitle.includes('BookingFlows') ? baseTitle : `${baseTitle} | BookingFlows`;
    setMeta('description', post.seo_description || post.excerpt);
    setMeta('keywords', post.seo_keywords);
    setMeta('og:title', post.seo_title || post.title, 'property');
    setMeta('og:description', post.seo_description || post.excerpt, 'property');
    setMeta('og:type', 'article', 'property');
    const heroAbs = post.hero_image_url
      ? (post.hero_image_url.startsWith('http') ? post.hero_image_url : `https://bookingflows.com${post.hero_image_url}`)
      : null;
    if (heroAbs) setMeta('og:image', heroAbs, 'property');
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `https://bookingflows.com/blog/${post.slug}`;
    // JSON-LD Article schema
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.id = 'platform-blog-ld';
    ld.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.seo_description || post.excerpt,
      image: heroAbs || undefined,
      datePublished: post.published_at,
      author: { '@type': 'Organization', name: 'BookingFlows', url: 'https://bookingflows.com' },
      publisher: { '@type': 'Organization', name: 'BookingFlows', logo: { '@type': 'ImageObject', url: 'https://bookingflows.com/logo-blue-512.png' } },
      mainEntityOfPage: `https://bookingflows.com/blog/${post.slug}`,
    });
    document.getElementById('platform-blog-ld')?.remove();
    document.head.appendChild(ld);
    return () => document.getElementById('platform-blog-ld')?.remove();
  }, [post]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4 text-center">
        <h1 className="text-xl font-bold text-gray-900 mb-2">Post not found</h1>
        <p className="text-gray-500 text-sm mb-6">This article may have moved.</p>
        <Link to="/blog" className="text-blue-600 font-semibold text-sm" data-testid="platform-blog-back-link">← Back to the blog</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="platform-blog-post">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 pt-[env(safe-area-inset-top)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/blog')} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" data-testid="platform-blog-nav-back">
                <ChevronLeft className="w-5 h-5 text-gray-500" />
              </button>
              <Link to="/" className="flex items-center gap-2">
                <img src="/logo-icon.png" alt="BookingFlows" className="h-7 w-7 object-contain rounded-lg" />
                <span className="text-base font-semibold text-gray-900 hidden sm:inline">BookingFlows</span>
              </Link>
            </div>
            <button
              onClick={() => navigate('/signup')}
              className="px-4 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-full hover:bg-blue-700 transition-colors"
              data-testid="platform-blog-signup-cta"
            >
              Sign up free
            </button>
          </div>
        </div>
      </nav>

      <main className="flex-1 w-full">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
          <div className="mb-6">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-bold uppercase tracking-wide" data-testid="platform-blog-category">
              {CATEGORY_LABELS[post.category] || post.category}
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight mb-4" data-testid="platform-blog-title">{post.title}</h1>
          <div className="flex items-center gap-3 text-sm text-gray-400 mb-8">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {post.published_at && new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
            <span>·</span>
            <span>By the BookingFlows team</span>
          </div>

          {post.hero_image_url && (
            <img
              src={post.hero_image_url}
              alt={post.title}
              className="w-full rounded-2xl mb-10 border border-gray-100"
              loading="lazy"
            />
          )}

          <div className="prose prose-slate max-w-none prose-headings:font-bold prose-h2:text-2xl prose-h3:text-xl prose-a:text-blue-600 prose-li:marker:text-blue-500 text-gray-700 leading-relaxed [&>p]:mb-5 [&>h2]:mt-10 [&>h2]:mb-4 [&>h3]:mt-8 [&>h3]:mb-3 [&>ul]:mb-5 [&>ul]:pl-6 [&>ul]:list-disc [&>ol]:mb-5 [&>ol]:pl-6 [&>ol]:list-decimal [&_li]:mb-1.5" data-testid="platform-blog-content">
            <ReactMarkdown>{post.content_md}</ReactMarkdown>
          </div>

          {/* End-of-post CTA — referral post drives to the program page */}
          {post.slug === 'referral-program' ? (
            <div className="mt-12">
              <button
                onClick={() => navigate('/referral-program')}
                className="w-full flex items-center gap-3 p-5 rounded-2xl bg-emerald-600 hover:bg-emerald-700 transition-colors text-left"
                data-testid="platform-blog-referral-cta"
              >
                <span className="w-10 h-10 rounded-xl bg-white/15 text-white flex items-center justify-center flex-shrink-0"><Rocket className="w-5 h-5" /></span>
                <span>
                  <span className="block text-sm font-bold text-white">Apply to the referral program</span>
                  <span className="block text-xs text-emerald-100">Earn 10–40% of the profit for a full year — anyone can join</span>
                </span>
              </button>
            </div>
          ) : (
          <div className="mt-12 grid sm:grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/support')}
              className="flex items-center gap-3 p-5 rounded-2xl border border-gray-200 hover:border-blue-200 hover:bg-blue-50/40 transition-colors text-left"
              data-testid="platform-blog-support-cta"
            >
              <span className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0"><LifeBuoy className="w-5 h-5" /></span>
              <span>
                <span className="block text-sm font-bold text-gray-900">Learn more</span>
                <span className="block text-xs text-gray-500">Step-by-step guides in the Help Center</span>
              </span>
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="flex items-center gap-3 p-5 rounded-2xl bg-blue-600 hover:bg-blue-700 transition-colors text-left"
              data-testid="platform-blog-signup-cta-bottom"
            >
              <span className="w-10 h-10 rounded-xl bg-white/15 text-white flex items-center justify-center flex-shrink-0"><Rocket className="w-5 h-5" /></span>
              <span>
                <span className="block text-sm font-bold text-white">Sign up free</span>
                <span className="block text-xs text-blue-100">Live in 15 minutes — no credit card</span>
              </span>
            </button>
          </div>
          )}

          {/* Related */}
          {post.related?.length > 0 && (
            <div className="mt-14">
              <h2 className="text-lg font-bold text-gray-900 mb-4">More from BookingFlows</h2>
              <div className="space-y-3">
                {post.related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:border-blue-200 hover:bg-blue-50/30 transition-colors group"
                    data-testid={`platform-blog-related-${r.slug}`}
                  >
                    <span>
                      <span className="block text-sm font-semibold text-gray-900 group-hover:text-blue-700">{r.title}</span>
                      <span className="block text-xs text-gray-400 mt-0.5">{CATEGORY_LABELS[r.category] || r.category}</span>
                    </span>
                    <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-blue-500 flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </main>

      <footer className="py-8 px-4 bg-white border-t border-gray-100 mt-auto">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo-icon.png" alt="BookingFlows" className="h-6 w-6 object-contain rounded-lg" />
            <span className="text-sm font-semibold text-gray-900">BookingFlows</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-500">
            <Link to="/blog" className="hover:text-gray-900 transition-colors">Blog</Link>
            <Link to="/support" className="hover:text-gray-900 transition-colors">Help Center</Link>
            <Link to="/signup" className="hover:text-gray-900 transition-colors">Sign up</Link>
          </div>
          <p className="text-xs text-gray-400">© {new Date().getFullYear()} BookingFlows</p>
        </div>
      </footer>
    </div>
  );
}
